// A fake mailbox that implements EXACTLY the graphMail.js interface.
//
// Why this exists: the agent workflow and the Outlook connection are two independent things, and only
// one of them is interesting. This lets you prove the workflow — inquiry in, gaps detected, reply
// drafted, customer answers, flow re-runs, trip sheet completes — with no Azure app registration and
// no Microsoft account. Swap it for the real thing later by flipping one env var; nothing else in the
// page changes.
//
// What is fake: ONLY the mail transport. The flow, the LLM extraction, the gap detection and the
// composed reply are all real and run against a real ECAP session. If the intake is wrong you will
// see it here exactly as you would over Outlook.
//
// The mailbox is seeded with ordinary business mail as well as charter inquiries, because the whole
// point of the "This is a trip inquiry" button is that a real mailbox is mostly everything else.
//
// State lives in localStorage so it survives a reload; resetMailbox() puts it back to the seed.

const STORE_KEY = 'krapf.mockMailbox.v1';
export const MOCK_ACCOUNT = { username: 'charters@krapfbus.example', name: 'Krapf Charters' };

const DAY = 86400000;
const iso = (offsetMs) => new Date(Date.now() - offsetMs).toISOString();

// A deliberately INCOMPLETE one-way inquiry: it carries the group, the destination, the date and a
// rough headcount, but no departure time, no phone and no booker email in the body — so the agent has
// real gaps to find rather than a tidy fixture that makes it look clever.
function seed() {
  const year = new Date().getFullYear() + 1;
  return [
    {
      conversationId: 'c-ridgefield',
      subject: 'Bus for the Lakemont game 11/21',
      messages: [{
        id: 'm-ridgefield-1',
        conversationId: 'c-ridgefield',
        subject: 'Bus for the Lakemont game 11/21',
        fromName: 'Ryan Mercer',
        fromAddress: 'rmercer@ridgefield.edu',
        receivedAt: iso(2 * 3600000),
        body: `Hi,

We need a coach for our hockey team's game at Lakemont Ice Arena on Saturday, November 21, ${year}.
Leaving from Ridgefield Rink. About 30 people plus hockey gear.

Thanks,
Ryan Mercer
Assistant Coach, Men's Ice Hockey
Ridgefield College`,
        bodyIsHtml: false,
        isDraft: false,
        headers: {},
        outgoing: false,
      }],
    },
    {
      conversationId: 'c-wcasd',
      subject: 'Quote request - Harrisburg field trip',
      messages: [{
        id: 'm-wcasd-1',
        conversationId: 'c-wcasd',
        subject: 'Quote request - Harrisburg field trip',
        fromName: 'Dana Whitfield',
        fromAddress: 'dwhitfield@wcasd.net',
        receivedAt: iso(26 * 3600000),
        body: `Hello,

Looking for a coach for a field trip to the State Museum in Harrisburg on May 14, ${year}.
We'll have 45 students and 5 chaperones, leaving from Henderson High School in West Chester at 7:00am.
Best number for me is 610-555-0144.

Thanks,
Dana Whitfield
West Chester Area School District`,
        bodyIsHtml: false,
        isDraft: false,
        headers: {},
        outgoing: false,
      }],
    },
    // A ROUND TRIP, and one that gives the return day in words ("bring us back that evening")
    // rather than a date — which is exactly the case the return-leg fields have to handle.
    {
      conversationId: 'c-shanahan',
      subject: 'Bus for the tournament 3/20',
      messages: [{
        id: 'm-shanahan-1',
        conversationId: 'c-shanahan',
        subject: 'Bus for the tournament 3/20',
        fromName: 'Coach Alvarez',
        fromAddress: 'j.alvarez@shanahan.org',
        receivedAt: iso(3 * 3600000),
        body: `Hi,

Need a bus for the team on March 20, ${year} - leave Bishop Shanahan High School in Downingtown at
8:00am for the tournament at Cumberland Valley High School in Mechanicsburg, and bring us back that
evening. 48 players and staff.

Thanks,
Coach Alvarez`,
        bodyIsHtml: false,
        isDraft: false,
        headers: {},
        outgoing: false,
      }],
    },
    // Noise — not charter business. Proves the operator's judgement is doing real work.
    {
      conversationId: 'c-invoice',
      subject: 'Invoice 20418 is ready',
      messages: [{
        id: 'm-invoice-1',
        conversationId: 'c-invoice',
        subject: 'Invoice 20418 is ready',
        fromName: 'Billing',
        fromAddress: 'ar@fleetservices.example',
        receivedAt: iso(3 * DAY),
        body: 'Your monthly statement is attached. No action needed if already paid.',
        bodyIsHtml: false,
        isDraft: false,
        headers: {},
        outgoing: false,
      }],
    },
    {
      conversationId: 'c-ooo',
      subject: 'Automatic reply: out of office',
      messages: [{
        id: 'm-ooo-1',
        conversationId: 'c-ooo',
        subject: 'Automatic reply: out of office',
        fromName: 'Pat Nolan',
        fromAddress: 'pnolan@example.org',
        receivedAt: iso(4 * DAY),
        body: 'I am out of the office until Monday with limited access to email.',
        bodyIsHtml: false,
        isDraft: false,
        // The mail-loop guard should treat this as auto-submitted.
        headers: { 'Auto-Submitted': 'auto-replied' },
        outgoing: false,
      }],
    },
  ];
}

function load() {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { /* fall through to seed */ }
  const s = seed();
  save(s);
  return s;
}

function save(threads) {
  try { window.localStorage.setItem(STORE_KEY, JSON.stringify(threads)); } catch (e) { /* private mode */ }
}

export function resetMailbox() {
  const s = seed();
  save(s);
  return s;
}

// ---- the graphMail interface -----------------------------------------------------------------

export function configure() { return true; }
export function isConfigured() { return true; }
export async function currentAccount() {
  try { return window.localStorage.getItem('krapf.mockSignedIn') === '1' ? MOCK_ACCOUNT : null; }
  catch (e) { return null; }
}
export async function signIn() {
  try { window.localStorage.setItem('krapf.mockSignedIn', '1'); } catch (e) { /* ignore */ }
  return MOCK_ACCOUNT;
}
export async function signOut() {
  try { window.localStorage.removeItem('krapf.mockSignedIn'); } catch (e) { /* ignore */ }
}

export async function listThreads() {
  return load()
    .map((t) => {
      const sorted = t.messages.slice().sort((a, b) => a.receivedAt.localeCompare(b.receivedAt));
      return { ...t, messages: sorted, latest: sorted[sorted.length - 1] };
    })
    .sort((a, b) => b.latest.receivedAt.localeCompare(a.latest.receivedAt));
}

export async function getThread(conversationId) {
  const t = load().find((x) => x.conversationId === conversationId);
  return t ? t.messages.slice().sort((a, b) => a.receivedAt.localeCompare(b.receivedAt)) : [];
}

/** Our outgoing reply. Appended to the thread so it renders like a real sent message. */
export async function createReplyDraft(messageId, bodyText) {
  const threads = load();
  const thread = threads.find((t) => t.messages.some((m) => m.id === messageId));
  if (!thread) throw new Error('Mock mailbox: no such message.');
  const id = `m-draft-${Date.now()}`;
  thread.messages.push({
    id,
    conversationId: thread.conversationId,
    subject: `Re: ${thread.subject}`,
    fromName: MOCK_ACCOUNT.name,
    fromAddress: MOCK_ACCOUNT.username,
    receivedAt: new Date().toISOString(),
    body: bodyText,
    bodyIsHtml: false,
    isDraft: true,
    headers: {},
    outgoing: true,
  });
  save(threads);
  return id;
}

export async function sendDraft(draftId) {
  const threads = load();
  threads.forEach((t) => t.messages.forEach((m) => { if (m.id === draftId) m.isDraft = false; }));
  save(threads);
}

export async function discardDraft(draftId) {
  const threads = load();
  threads.forEach((t) => { t.messages = t.messages.filter((m) => m.id !== draftId); });
  save(threads);
}

// ---- mock-only controls (the test harness) ----------------------------------------------------

/** Drop a brand-new inquiry into the inbox, as if a customer just emailed in. */
export function injectInquiry({ fromName, fromAddress, subject, body }) {
  const threads = load();
  const conversationId = `c-${Date.now()}`;
  threads.push({
    conversationId,
    subject,
    messages: [{
      id: `m-${Date.now()}`,
      conversationId,
      subject,
      fromName: fromName || 'Test Customer',
      fromAddress: fromAddress || 'customer@example.com',
      receivedAt: new Date().toISOString(),
      body,
      bodyIsHtml: false,
      isDraft: false,
      headers: {},
      outgoing: false,
    }],
  });
  save(threads);
  return conversationId;
}

/**
 * Simulate the customer answering. This is the piece that makes the whole loop testable: the page's
 * poll sees a new inbound message on a watched thread and feeds it to the flow as the next turn,
 * exactly as it would with a real reply from Outlook.
 */
export function replyAsCustomer(conversationId, body) {
  const threads = load();
  const thread = threads.find((t) => t.conversationId === conversationId);
  if (!thread) throw new Error('Mock mailbox: no such thread.');
  const original = thread.messages.find((m) => !m.outgoing) || thread.messages[0];
  thread.messages.push({
    id: `m-reply-${Date.now()}`,
    conversationId,
    subject: `Re: ${thread.subject}`,
    fromName: original.fromName,
    fromAddress: original.fromAddress,
    receivedAt: new Date().toISOString(),
    body,
    bodyIsHtml: false,
    isDraft: false,
    headers: {},
    outgoing: false,
  });
  save(threads);
}
